import { isPlatformBrowser } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	HostListener,
	inject,
	Input,
	input,
	OnChanges,
	OnDestroy,
	PLATFORM_ID,
	Signal,
	SimpleChanges,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { createPopper, Instance, Placement } from '@popperjs/core';

import { cn } from '@valk-nx/core/lib/core';
import { Debounce } from '@valk-nx/core/lib/decorators/debounce';
import { GeneralHelper } from '@valk-nx/helpers/lib/general/general.helper';
import { ViewPortService } from '@valk-nx/services/viewport/src/lib/viewport.service';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'vp-tooltip',
	templateUrl: './tooltip.component.html',
	standalone: false,
	styleUrls: ['./tooltip.component.scss'],
})
export class TooltipComponent implements AfterViewInit, OnChanges, OnDestroy {
	private readonly platformId = inject(PLATFORM_ID);
	private readonly viewport = inject(ViewPortService);

	@Input() alignment: Placement = 'top';
	@Input() showTooltip = false;
	@Input() iconSize = 'w-6';
	@Input() tooltipColor = 'text-black';
	@Input() ariaLabel = '';
	fixedHeight = input<boolean>(true);

	isMobile: Signal<boolean | undefined>;

	uuid = GeneralHelper.generateRandomId();
	tooltipContentId = 'tooltip-content-' + this.uuid;
	tooltipTriggerId = 'tooltip-trigger-' + this.uuid;
	tooltipArrowId = 'tooltip-arrow-' + this.uuid;

	popperInstance!: Instance;
	popoverInstance!: HTMLElement;
	triggerInstance!: Element;
	arrowInstance!: HTMLElement;
	scrollPosition = 0;

	cn = cn;

	constructor() {
		this.isMobile = toSignal(this.viewport.isMobile$);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['alignment'] && !changes['alignment']['firstChange']) {
			this.updateTooltip();
		}
	}

	ngAfterViewInit(): void {
		/* istanbul ignore else */
		if (isPlatformBrowser(this.platformId)) {
			this.popoverInstance = document.getElementById(
				this.tooltipContentId,
			) as HTMLElement;
			this.triggerInstance = document.getElementById(
				this.tooltipTriggerId,
			) as Element;
			this.arrowInstance = document.getElementById(
				this.tooltipArrowId,
			) as HTMLElement;

			this.popperInstance = createPopper(
				this.triggerInstance,
				this.popoverInstance,
				{
					modifiers: [
						{
							name: 'offset',
							options: {
								offset: [0, 8],
							},
						},
						{
							name: 'arrow',
							options: {
								element: this.arrowInstance,
								padding: 12,
							},
						},
						{ enabled: true, name: 'flip' },
					],
					placement: this.alignment,
				},
			);
		}
	}

	ngOnDestroy() {
		/* istanbul ignore else */
		if (isPlatformBrowser(this.platformId)) {
			this.popperInstance.destroy();
		}
	}

	@Debounce(300)
	@HostListener('window:resize')
	resizeTooltip() {
		if (this.showTooltip) {
			this.hide();
		}
		this.updateTooltip();
	}

	updateTooltip() {
		this.popperInstance.setOptions({
			placement: this.alignment,
		});
		this.popperInstance.forceUpdate();
	}

	show() {
		this.updateTooltip();
		this.showTooltip = true;
		this.scrollPosition = window.scrollY;
		if (this.isMobile()) {
			GeneralHelper.mobileBodyLock(true, this.scrollPosition, true);
		}
	}

	hide() {
		this.showTooltip = false;
		GeneralHelper.mobileBodyLock(false, this.scrollPosition, true);
	}

	toggle() {
		if (this.showTooltip) {
			this.hide();
		} else {
			this.show();
		}
	}

	hideAndStopPropagation(event: Event) {
		event.stopPropagation();
		event.preventDefault();
		this.hide();
	}
}
